// Soạn thông báo gửi cấp dưới (docs/KE_HOACH_GUI_THONG_BAO.md §3) — dùng chung khu Đại lý / CTV và khu Admin NPP.
// Người nhận tính ở máy chủ: form chỉ gửi bộ lọc (Tất cả / Theo nhánh / Chọn đơn vị + loại), số người nhận lấy từ
// /preview (chờ 400ms sau lần đổi cuối). Gửi → hộp xác nhận với số mới nhất → gửi → làm trống form.

import { useId, useMemo, useState, type ReactNode } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { buildNetworkTree, networkCountsText, networkUnitTitle } from '@shoptrack/common/network-tree'
import { useDebounced } from '@/hooks/useDebounced'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Section } from '@/components/ui/section'
import { confirm } from '@/components/ui/confirm'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ANNOUNCE_KIND, AnnouncementView, type AnnounceKind } from './AnnouncementView'
import { UnitPicker, type PickerItem } from './UnitPicker'
import {
  BODY_MAX, TITLE_MAX, UNIT_TYPE_LABEL, buildAudience, expiresIso, fmtNum, linkError, todayVN,
  type AnnPreview, type AnnounceApi, type AnnounceScope, type AnnounceUnit, type AnnounceUnitType, type Audience, type SendBody,
} from './types'

const KINDS: AnnounceKind[] = ['warning', 'promo', 'info']
const SCOPE_LABEL: Record<AnnounceScope, string> = { all: 'Tất cả', branches: 'Theo nhánh', units: 'Chọn đơn vị' }

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback)

const previewKey = (api: AnnounceApi, audienceJson: string) => [api.area, 'announce-preview', audienceJson] as const

export function AnnounceComposer({
  api,
  types,
  branchTypes,
  units,
  unitsLoading,
  unitsError,
  onRetryUnits,
  senderName,
}: {
  api: AnnounceApi
  /** Loại đơn vị được gửi tới (Đại lý: CTV, Shop; CTV: Shop; NPP: cả ba) */
  types: AnnounceUnitType[]
  /** Loại đơn vị làm gốc "Theo nhánh" — rỗng thì không có "Theo nhánh" */
  branchTypes: AnnounceUnitType[]
  /** Đơn vị trong phạm vi người gửi (không gồm chính mình) — để chọn nhánh / từng đơn vị */
  units: AnnounceUnit[]
  unitsLoading?: boolean
  unitsError?: boolean
  onRetryUnits?: () => void
  /** Tên người gửi người nhận thấy — cho "Xem trước" */
  senderName: string
}) {
  const qc = useQueryClient()
  const ids = { title: useId(), body: useId(), link: useId(), exp: useId() }

  const [kind, setKind] = useState<AnnounceKind>('info')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [link, setLink] = useState('')
  const [expires, setExpires] = useState('')
  const [scope, setScope] = useState<AnnounceScope>('all')
  const [selTypes, setSelTypes] = useState<AnnounceUnitType[]>(types)
  const [branchIds, setBranchIds] = useState<string[]>([])
  const [unitIds, setUnitIds] = useState<string[]>([])
  const [includeStaff, setIncludeStaff] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  // Danh sách chọn: nhánh = đơn vị loại gốc nhánh (kèm số đơn vị bên dưới); từng đơn vị = mọi đơn vị (kèm cấp trên).
  const { branchItems, unitItems } = useMemo(() => {
    const allowed = units.filter((u) => types.includes(u.tenantType))
    const tree = buildNetworkTree(units)
    const search = (u: AnnounceUnit) => [u.owner?.name, u.owner?.phone]
    const unitItems: PickerItem[] = []
    const branchItems: PickerItem[] = []
    for (const u of allowed) {
      const node = tree.byId.get(u.id)
      const parent = node?.parent?.unit
      unitItems.push({ ...u, sub: parent ? networkUnitTitle(parent) : u.owner?.phone ?? undefined, search: search(u) })
      if (branchTypes.includes(u.tenantType)) {
        branchItems.push({ ...u, sub: node ? networkCountsText(node.below, '') || undefined : undefined, search: search(u) })
      }
    }
    const order = (a: PickerItem, b: PickerItem) =>
      types.indexOf(a.tenantType) - types.indexOf(b.tenantType) || (a.name ?? '').localeCompare(b.name ?? '', 'vi')
    return { branchItems: branchItems.sort(order), unitItems: unitItems.sort(order) }
  }, [units, types, branchTypes])

  const scopes: AnnounceScope[] = branchTypes.length && (unitsLoading || branchItems.length) ? ['all', 'branches', 'units'] : ['all', 'units']
  const showTypes = scope !== 'units' && types.length > 1

  const audience = useMemo(
    () => buildAudience({ scope, types: types.length > 1 ? selTypes : types, branchIds, unitIds, includeStaff }),
    [scope, types, selTypes, branchIds, unitIds, includeStaff],
  )
  // Chờ theo chuỗi JSON (không theo object) — trang cha vẽ lại không làm đồng hồ chờ chạy lại
  const audienceJson = audience ? JSON.stringify(audience) : ''
  const debouncedJson = useDebounced(audienceJson, 400)
  const previewFn = (json: string) => () => api.post<AnnPreview>(`${api.base}/preview`, { audience: JSON.parse(json) as Audience })
  const preview = useQuery({
    queryKey: previewKey(api, debouncedJson),
    queryFn: previewFn(debouncedJson),
    enabled: !!debouncedJson,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false,
  })
  const settling = !!audience && (audienceJson !== debouncedJson || preview.isFetching)

  const titleErr = submitted && !title.trim() ? 'Nhập tiêu đề' : null
  const bodyErr = submitted && !body.trim() ? 'Nhập nội dung' : null
  const linkErr = submitted ? linkError(link) : null
  const audienceErr = submitted && !audience ? (scope === 'branches' ? 'Chọn nhánh' : scope === 'units' ? 'Chọn đơn vị' : 'Chọn người nhận') : null

  const reset = () => {
    setKind('info'); setTitle(''); setBody(''); setLink(''); setExpires('')
    setScope('all'); setSelTypes(types); setBranchIds([]); setUnitIds([]); setIncludeStaff(false)
    setSubmitted(false)
  }

  const send = useMutation({
    mutationFn: (b: SendBody) => api.post<{ id: string; recipients: number; units: number }>(api.base, b),
    onSuccess: () => {
      toast.success('Đã gửi')
      reset()
      qc.invalidateQueries({ queryKey: [api.area, 'announcements'] })
    },
    onError: (e) => toast.error(errText(e, 'Không gửi được')),
  })

  async function onSend() {
    setSubmitted(true)
    if (!title.trim() || !body.trim() || linkError(link) || !audience) return
    let p: AnnPreview
    try {
      p = await qc.fetchQuery({ queryKey: previewKey(api, audienceJson), queryFn: previewFn(audienceJson), staleTime: 5_000, retry: false })
    } catch (e) {
      toast.error(errText(e, 'Không tính được người nhận'))
      return
    }
    if (!p.recipients) {
      toast.error('Không có người nhận')
      return
    }
    const ok = await confirm({ title: `Gửi tới ${fmtNum(p.recipients)} người ở ${fmtNum(p.units)} đơn vị?`, confirmLabel: 'Gửi' })
    if (!ok) return
    send.mutate({
      kind,
      title: title.trim(),
      body: body.trim(),
      linkUrl: link.trim() || null,
      expiresAt: expiresIso(expires),
      audience,
    })
  }

  const countLine = !audience
    ? `0 đơn vị · 0 người`
    : preview.data
      ? `${fmtNum(preview.data.units)} đơn vị · ${fmtNum(preview.data.recipients)} người`
      : '… đơn vị · … người'

  return (
    <Section>
      <div className="grid gap-6 p-4 desktop:p-6 desktop:xl:grid-cols-2 desktop:xl:gap-8">
        {/* ─── Nội dung ─── */}
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-3 gap-2" role="group" aria-label="Loại">
            {KINDS.map((k) => {
              const meta = ANNOUNCE_KIND[k]
              const Icon = meta.icon
              const on = kind === k
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setKind(k)}
                  className={cn(
                    'motion-state flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-2 text-sm font-medium desktop:min-h-10 mobile:min-h-14 mobile:flex-col mobile:gap-1 mobile:py-2 mobile:text-xs',
                    on ? cn(meta.badge, 'border-current') : 'border-rule bg-raise text-inkSoft hover:bg-paperSoft',
                  )}
                >
                  <Icon className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{meta.label}</span>
                </button>
              )
            })}
          </div>

          <Field id={ids.title} label="Tiêu đề" count={`${title.length}/${TITLE_MAX}`} error={titleErr}>
            <Input id={ids.title} value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} aria-invalid={titleErr ? true : undefined} />
          </Field>

          <Field id={ids.body} label="Nội dung" count={`${body.length}/${BODY_MAX}`} error={bodyErr}>
            <Textarea id={ids.body} value={body} maxLength={BODY_MAX} rows={6} onChange={(e) => setBody(e.target.value)} aria-invalid={bodyErr ? true : undefined} />
          </Field>

          <div className="grid gap-4 desktop:grid-cols-[minmax(0,1fr)_auto]">
            <Field id={ids.link} label="Link" error={linkErr}>
              <Input id={ids.link} value={link} maxLength={500} inputMode="url" autoComplete="off" placeholder="https://" onChange={(e) => setLink(e.target.value)} aria-invalid={linkErr ? true : undefined} />
            </Field>
            <Field id={ids.exp} label="Hết hạn">
              <div className="flex items-center gap-1">
                <Input id={ids.exp} type="date" value={expires} min={todayVN()} onChange={(e) => setExpires(e.target.value)} className="desktop:w-44" />
                {expires && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => setExpires('')} aria-label="Xóa ngày hết hạn">
                    <X />
                  </Button>
                )}
              </div>
            </Field>
          </div>
        </div>

        {/* ─── Người nhận ─── */}
        <fieldset className="min-w-0 space-y-3">
          <legend className="mb-1.5 text-sm text-inkSoft">Người nhận</legend>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Người nhận">
            {scopes.map((s) => (
              <Button key={s} type="button" size="sm" aria-pressed={scope === s} variant={scope === s ? 'default' : 'outline'} onClick={() => setScope(s)}>
                {SCOPE_LABEL[s]}
              </Button>
            ))}
          </div>

          {showTypes && (
            <div className="flex flex-wrap gap-x-4 gap-y-1" role="group" aria-label="Loại đơn vị">
              {types.map((t) => (
                <CheckRow
                  key={t}
                  checked={selTypes.includes(t)}
                  onChange={(on) => setSelTypes((cur) => (on ? [...cur, t] : cur.filter((x) => x !== t)))}
                >
                  {UNIT_TYPE_LABEL[t]}
                </CheckRow>
              ))}
            </div>
          )}

          {scope === 'branches' && (
            <UnitPicker title="Chọn nhánh" items={branchItems} selected={branchIds} onChange={setBranchIds} loading={unitsLoading} error={unitsError} onRetry={onRetryUnits} />
          )}
          {scope === 'units' && (
            <UnitPicker title="Chọn đơn vị" items={unitItems} selected={unitIds} onChange={setUnitIds} loading={unitsLoading} error={unitsError} onRetry={onRetryUnits} />
          )}

          <CheckRow checked={includeStaff} onChange={setIncludeStaff}>Gửi cả nhân viên</CheckRow>

          {audienceErr && <p className="text-xs text-danger" role="alert">{audienceErr}</p>}
        </fieldset>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-rule px-4 py-3 desktop:px-6">
        <p className={cn('min-w-0 text-sm tabular-nums text-ink mobile:w-full', settling && 'opacity-60')} aria-live="polite">
          {audience && preview.isError && !preview.isFetching ? (
            <span className="text-danger">{errText(preview.error, 'Không tính được người nhận')}</span>
          ) : (
            countLine
          )}
        </p>
        <span className="flex-1 mobile:hidden" />
        <Button type="button" variant="outline" className="mobile:flex-1" onClick={() => setPreviewOpen(true)} disabled={!title.trim() && !body.trim()}>
          Xem trước
        </Button>
        <Button type="button" className="mobile:flex-1" onClick={onSend} disabled={send.isPending}>
          {send.isPending ? 'Đang gửi…' : 'Gửi'}
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="pr-8">
            <DialogTitle>Xem trước</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg border border-rule p-4">
            <AnnouncementView a={{ kind, title: title.trim(), message: body.trim(), sender: senderName, createdAt: new Date(), url: link.trim() && !linkError(link) ? link.trim() : null }} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  )
}

function Field({ id, label, count, error, children }: { id: string; label: string; count?: string; error?: string | null; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm text-inkSoft">{label}</label>
        {count && <span className="text-xs tabular-nums text-muted">{count}</span>}
      </div>
      {children}
      {error && <p className="text-xs text-danger" role="alert">{error}</p>}
    </div>
  )
}

/** Ô tick kèm chữ — cả hàng là vùng bấm, cao 44px trên điện thoại. */
function CheckRow({ checked, onChange, children }: { checked: boolean; onChange: (on: boolean) => void; children: ReactNode }) {
  return (
    <label className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-ink desktop:min-h-8">
      <input type="checkbox" className="size-4 shrink-0 cursor-pointer accent-accent" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  )
}
